import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, useFormState } from 'react-final-form';
import RadioGroupField from 'components/RadioGroupField';
import useMatchMedia from 'hooks/useMatchMedia';
import CheckToggleField from 'components/CheckToggleField';
import {
  RadioOptionNotificationProps,
  TemporaryNotificationsProps,
} from './types';
import { TEMPORARY_NOTIFICATIONS_OPTIONS } from './data';
import ProfileActions from '../../components/ProfileActions';
import CurrentProfileContext from '../../providers/CurrentProfile/CurrentProfile.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

import './TemporaryNotifications.styles.scss';
import './TemporaryNotifications.styles.responsive.scss';

const RadioOptionNotification: React.FC<RadioOptionNotificationProps> = ({
  icon,
  text,
  description,
}) => {
  const { t } = useTranslation();
  return (
    <div className="radio-option-notification">
      {icon}
      <span>{t(text)}</span>
      <span>{t(description)}</span>
    </div>
  );
};

const TemporaryNotifications: React.FC<TemporaryNotificationsProps> = (
  props,
) => {
  const { className } = props;
  const isPhablet = useMatchMedia('isPhablet');
  const { t } = useTranslation();
  const {
    values: { enablePollEmailNotifications, enablePollSmsNotifications },
  } = useFormState();

  const classes = classNames(
    'temporary-notifications',
    'profile-card-container',
    className,
  );

  const { isMyProfile } = useContext(CurrentProfileContext);
  const {
    currentUser: { hasVerifiedEmail, hasVerifiedContactNumber },
    openVerificationModal,
    openPhoneVerificationModal,
  } = useContext(CurrentUserContext);

  const shouldOpenEmailVerificationModal = useMemo(
    () => isMyProfile && !hasVerifiedEmail && !enablePollEmailNotifications,
    [enablePollEmailNotifications, hasVerifiedEmail, isMyProfile],
  );

  const shouldOpenPhoneVerificationModal = useMemo(
    () =>
      isMyProfile && !hasVerifiedContactNumber && !enablePollSmsNotifications,
    [enablePollSmsNotifications, hasVerifiedContactNumber, isMyProfile],
  );

  return (
    <div className={classes}>
      <p className="profile-section-title">{t('notifications')}</p>

      <RadioGroupField
        disabled={!enablePollEmailNotifications && !enablePollSmsNotifications}
        name="pollNotificationIntensity"
        noValidate
        styled
        direction={isPhablet ? 'column' : 'row'}
        options={TEMPORARY_NOTIFICATIONS_OPTIONS.map(({ value, props }) => ({
          value,
          label: <RadioOptionNotification {...props} />,
        }))}
      />

      <div className="button-toggles-container">
        <div>
          <Field
            name="enablePollEmailNotifications"
            type="checkbox"
            label={t('acceptEmailNotifications')}
            component={CheckToggleField}
            {...(shouldOpenEmailVerificationModal && {
              onChange: () => openVerificationModal(),
            })}
          />
          <Field
            name="enablePollSmsNotifications"
            type="checkbox"
            label={t('acceptSmsNotifications')}
            component={CheckToggleField}
            {...(shouldOpenPhoneVerificationModal && {
              onChange: () => openPhoneVerificationModal(),
            })}
          />
        </div>

        <ProfileActions />
      </div>
    </div>
  );
};

export default TemporaryNotifications;
